import { getVariableValue } from "@/shared/utils/resolve-tokens";
import type { PillDecorator } from "@/shared/utils/variable-pill";
import {
  isDateVariableName,
  isNowBasedDateName,
  resolveDateToken,
} from "./date-resolver";
import { parseDateToken } from "./date-token";

/**
 * Version-mode decorator for date pills: the pill shows the RESOLVED date —
 * the sender's value (or today for the built-in Time/Date group) with the
 * token's own offset and format applied — the same string the bake prints.
 * A sender date that is still empty keeps the raw token so it reads as
 * "not set" rather than as today. Returns undefined outside version mode
 * (no data) and for non-date names, so other decorators/plain pills apply.
 */
export const createDatePillDecorator = (
  data: Record<string, unknown> | undefined | null,
  extraDateNames?: ReadonlySet<string>,
  now: () => Date = () => new Date(),
): PillDecorator => {
  return (name) => {
    if (!data) return undefined;
    const bare = parseDateToken(name).name;
    if (!isDateVariableName(bare, extraDateNames)) return undefined;
    const baseValue = getVariableValue(data, bare);
    const hasValue = baseValue != null && baseValue !== "";
    if (!hasValue && !isNowBasedDateName(bare)) return { text: `{{${name}}}` };
    return { text: resolveDateToken(name, { now: now(), baseValue }) };
  };
};
